import type { ProductRelation, ProductRelations } from '../../types'
import { canonicalPath } from './seo'

/**
 * Discontinued → successor banner (T4.3 §6.1.2). Shown prominently on obsolete
 * products: the known successor(s) with an optional migration note, plus a
 * contact CTA. When no successor is known, the caller shows nothing here — the
 * hero already carries the honest "uitgefaseerd" message and the contact route.
 *
 * SSR-safe (no client state) so the successor link is in the server HTML — it's
 * exactly the path a customer who landed on a dead type number needs to follow.
 */
export default function SuccessorBanner({
  lang,
  relations,
}: {
  lang: string
  relations: ProductRelations | undefined
}) {
  const successors = (relations?.replaced_by ?? []).filter((r) => r.resolved != null)
  if (successors.length === 0) return null

  const note = (r: ProductRelation): string | null => {
    const n = r.migration_note
    if (!n) return null
    return (lang === 'en' ? n.en : n.nl) ?? n.nl ?? n.en ?? null
  }

  return (
    <section
      className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-6"
      aria-labelledby="successor-heading"
    >
      <h2 id="successor-heading" className="text-base font-bold text-amber-900">
        {lang === 'en' ? 'This product has a successor' : 'Dit product heeft een opvolger'}
      </h2>
      <p className="text-sm text-amber-800 mt-1 mb-4">
        {lang === 'en'
          ? 'This item is phased out. Order the successor below, or contact our engineer if you are unsure.'
          : 'Dit artikel is uitgefaseerd. Bestel hieronder de opvolger, of neem contact op met onze engineer als u twijfelt.'}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {successors.map((r) => {
          const target = r.resolved!
          const migration = note(r)
          return (
            <a
              key={r.target_mpn}
              href={canonicalPath(lang, target.slug)}
              className="flex items-start gap-3 rounded-lg border border-amber-200 bg-white p-3 hover:border-primary/40 hover:shadow-sm transition-all"
            >
              <img
                src={target.primary_image_url ?? '/placeholder-product.svg'}
                alt=""
                className="h-14 w-14 object-contain shrink-0"
              />
              <div className="min-w-0">
                <div className="font-mono text-xs text-neutral-500">{target.normalized_mpn}</div>
                <div className="text-sm font-semibold text-neutral-900 line-clamp-2">{target.display_title}</div>
                {migration && <div className="mt-1 text-xs text-amber-800">{migration}</div>}
              </div>
            </a>
          )
        })}
      </div>

      <a
        href={`/${lang}/contact`}
        className="mt-4 inline-flex text-sm font-medium text-primary hover:underline"
      >
        {lang === 'en' ? 'Ask an engineer →' : 'Vraag het een engineer →'}
      </a>
    </section>
  )
}
